import {Injectable} from '@angular/core';
import {HttpClient, HttpRequest, HttpEvent, HttpHeaders, HttpResponse, HttpEventType} from '@angular/common/http';
import {AsyncSubject, BehaviorSubject, from, Observable} from 'rxjs';
import * as JSZip from "jszip";
import {map} from "rxjs/operators";
import {Archivo} from "../../model/archivos/archivo.model";
import {ApiService} from "../../api/api.service";

@Injectable({
    providedIn: 'root'
})
export class FileUploadService {
    private uploadUrl = '';
    private zipName = 'VARIOS.zip';
    private httpHeaders: HttpHeaders;
    public progressUploadObserver: BehaviorSubject<number> = new BehaviorSubject<number>(0);
    public progressCompressObserver: BehaviorSubject<number> = new BehaviorSubject<number>(0);


    constructor(private http: HttpClient, private readonly _apiService: ApiService) {
    }

    upload(file: File, type: string = ''): Observable<HttpEvent<any>> {
        const formData: FormData = new FormData();
        formData.append('file', file);
        if (type !== '') {
            formData.append('type', type);
        }
        const req = new HttpRequest('POST', `${this.uploadUrl}`, formData, {
            headers: this.httpHeaders,
            reportProgress: true,
            responseType: 'json'
        });
        return this.http.request(req).pipe(map((x: HttpEvent<any>) => {
            if (x.type === HttpEventType.UploadProgress) {
                this.progressUploadObserver.next(Math.round(100 * x.loaded / x.total));
            }
            return x;
        }));
    }

    remove(file: Archivo): Observable<any> {
        return this._apiService.del(file.iri.toString());
    }

    /**
     * Un zip nuevo en cada llamada, generado cuando ya se han leído todos los archivos (menos los
     * ocultos). Antes devolvía un BehaviorSubject del servicio: quien se suscribía recibía primero el zip
     * del arrastre anterior (o null), y el zip se generaba al leer el último archivo aunque los demás no
     * hubieran terminado.
     */
    compress(arrayFiles: File[]): Observable<File> {
        const new_zip = new JSZip();
        const lecturas = arrayFiles
            .filter(file => file.name.substr(file.name.lastIndexOf('/') + 1, 1) !== '.')
            .map(file => file.arrayBuffer().then(contenido => {
                const path = file['webkitRelativePath'] ? file['webkitRelativePath'] : file.name;
                new_zip.file(path, contenido);
            }));
        return from(Promise.all(lecturas)
            .then(() => new_zip.generateAsync({
                type: "arraybuffer", compression: "DEFLATE", compressionOptions: {
                    level: 1 // force a compression and a compression level for this file
                }
            }, (metadata) => {
                this.progressCompressObserver.next(Math.round(metadata.percent));
            }))
            .then(arrayBuffer => new File([arrayBuffer], this.zipName)));
    }

    /**
     * Lo soltado en UN arrastre: el archivo, o un zip con todos si son varios. Emite una sola vez y
     * termina. La lista es de cada llamada: antes era del servicio, que es único para toda la app, y
     * nunca se vaciaba, así que cada arrastre volvía a subir los anteriores, también en otro pedido.
     */
    proccessDropEventFiles(arrayFiles: DataTransferItemList): Observable<File> {
        const filesDropped: File[] = [];
        const dropCompleted = new AsyncSubject<File>();
        let timeOut;
        // Se espera a que deje de llegar contenido (las carpetas se leen de forma asíncrona)
        const resetTimer = () => {
            clearTimeout(timeOut);
            timeOut = setTimeout(() => {
                if (filesDropped.length === 0) {
                    dropCompleted.complete();
                } else if (filesDropped.length === 1) {
                    dropCompleted.next(filesDropped[0]);
                    dropCompleted.complete();
                } else {
                    this.compress(filesDropped).subscribe(dropCompleted);
                }
            }, 1000);
        };

        // Los items solo se pueden leer mientras dura el evento drop: se recorren ya, no al suscribirse
        for (let i = 0; i < arrayFiles.length; i++) {
            const item = arrayFiles[i];
            if (item.kind === 'file') {
                const entry = item.getAsFile();
                if (entry) {
                    filesDropped.push(entry);
                    resetTimer();
                } else if (item.webkitGetAsEntry() && item.webkitGetAsEntry().isDirectory) {
                    this.getAllFilesFromDirectoryRecursive(item.webkitGetAsEntry(), filesDropped, resetTimer);
                }
            }
        }
        return dropCompleted.asObservable();
    }


    private getAllFilesFromDirectoryRecursive(item, filesDropped: File[], resetTimer: () => void) {
        resetTimer();
        if (item.isDirectory) {
            const directoryReader = item.createReader();
            directoryReader.readEntries((entries) => {
                entries.forEach((entry) => {
                    this.getAllFilesFromDirectoryRecursive(entry, filesDropped, resetTimer);
                });
            });
        } else {
            item.file((file) => {
                filesDropped.push(file);
            });
        }
    }


    setBaseUrl(url: string) {
        this.uploadUrl = url;
    }

    setRequestHeaders(params: HttpHeaders) {
        this.httpHeaders = params;
    }
}
